import { useEffect, useRef, useState } from "react";
import { IconButton, InputAdornment, TextField } from "@mui/material";
import { Clear as ClearIcon, Search as SearchIcon } from "@mui/icons-material";
import type { SxProps, Theme } from "@mui/material/styles";

interface DebouncedSearchFieldProps {
  initialValue?: string;
  delayMs?: number;
  placeholder?: string;
  onDebouncedChange: (value: string) => void;
  testId?: string;
  sx?: SxProps<Theme>;
}

export function DebouncedSearchField({
  initialValue = "",
  delayMs = 250,
  placeholder = "",
  onDebouncedChange,
  testId,
  sx,
}: DebouncedSearchFieldProps): React.ReactElement {
  const [value, setValue] = useState(initialValue);
  const onChangeRef = useRef(onDebouncedChange);
  onChangeRef.current = onDebouncedChange;
  const isFirstRef = useRef(true);

  useEffect(() => {
    if (isFirstRef.current) {
      isFirstRef.current = false;
      return;
    }
    const handle = setTimeout(() => onChangeRef.current(value), delayMs);
    return () => clearTimeout(handle);
  }, [value, delayMs]);

  return (
    <TextField
      size="small"
      placeholder={placeholder}
      value={value}
      onChange={(e) => setValue(e.target.value)}
      {...(testId ? { inputProps: { "data-testid": testId } } : {})}
      {...(sx ? { sx } : {})}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <SearchIcon color="action" />
          </InputAdornment>
        ),
        ...(value
          ? {
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setValue("")}>
                    <ClearIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }
          : {}),
      }}
    />
  );
}
